import { useState } from "react";
import type { Quote } from "@fa/shared";
import { uploadDocument, useApiBase, type StkStatus, type MemberDocument } from "../api";

/* ------------------------------------------------------------------ */
/*  Field — label + error + required indicator wrapper                 */
/* ------------------------------------------------------------------ */

export function Field({
  id,
  label,
  error,
  required,
  children,
}: {
  id: string;
  label: string;
  error?: string;
  required?: boolean;
  children: React.ReactNode;
}) {
  return (
    <div>
      <label htmlFor={id} className="fa-text-sm fa-font-medium fa-mb-1 fa-block">
        {label}
        {required && <span className="fa-text-red-300 fa-ml-0.5">*</span>}
      </label>
      <div className={error ? "fa-ring-2 fa-ring-red-400 fa-rounded-md" : ""}>{children}</div>
      {error && (
        <p className="fa-text-xs fa-text-red-200 fa-mt-0.5" role="alert">
          {error}
        </p>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  FormError — top-level error banner                                 */
/* ------------------------------------------------------------------ */

export function FormError({ message }: { message: string | null }) {
  if (!message) return null;
  return (
    <div
      role="alert"
      className="fa-bg-red-50 fa-border fa-border-red-300 fa-text-red-800 fa-rounded-md fa-p-3 fa-text-sm fa-my-3"
    >
      {message}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  SubmitButton — loading spinner + double-click protection           */
/* ------------------------------------------------------------------ */

export function SubmitButton({
  onClick,
  disabled,
  loading,
  children,
  variant = "primary",
}: {
  onClick: () => void;
  disabled?: boolean;
  loading?: boolean;
  children: React.ReactNode;
  variant?: "primary" | "orange";
}) {
  return (
    <button
      className={variant === "primary" ? "fa-btn-primary" : "fa-btn-orange"}
      disabled={disabled || loading}
      onClick={onClick}
    >
      {loading ? (
        <span className="fa-inline-flex fa-items-center fa-gap-2">
          <svg
            className="fa-animate-spin fa-h-4 fa-w-4"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
          >
            <circle className="fa-opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path className="fa-opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
          </svg>
          Processing…
        </span>
      ) : (
        children
      )}
    </button>
  );
}

/* ------------------------------------------------------------------ */
/*  PaymentStatus — M-Pesa status display                              */
/* ------------------------------------------------------------------ */

export function PaymentStatus({
  status,
  receipt,
  error,
}: {
  status: StkStatus | null;
  receipt: string | null;
  error: string | null;
}) {
  return (
    <>
      {error && (!status || status === "PENDING") && (
        <div className="fa-text-amber-700 fa-text-sm fa-mt-1">{error}</div>
      )}
      {status && (
        <div className="fa-text-sm fa-mt-1">
          {status === "PENDING" && (
            <span className="fa-inline-flex fa-items-center fa-gap-1">
              <svg
                className="fa-animate-spin fa-h-3 fa-w-3"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle className="fa-opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="fa-opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
              </svg>
              Waiting for you to complete the M-Pesa prompt on your phone…
            </span>
          )}
          {status === "SUCCESS" && (
            <span className="fa-text-green-700 fa-font-medium">
              ✓ Payment received{receipt ? ` — Receipt: ${receipt}` : ""}
            </span>
          )}
          {status === "FAILED" && (
            <span className="fa-text-red-700">
              Payment was not completed. You can try again or pay manually via Paybill.
            </span>
          )}
          {status === "CANCELLED" && (
            <span className="fa-text-amber-700">
              You cancelled the M-Pesa prompt. You can try again or pay manually via Paybill.
            </span>
          )}
          {status === "TIMEOUT" && (
            <span className="fa-text-amber-700">
              The M-Pesa prompt timed out. Please try again.
            </span>
          )}
          {status === "UNKNOWN" && (
            <span className="fa-text-slate-600">
              Unable to confirm payment status. If you completed the payment, please allow a few minutes for confirmation.
            </span>
          )}
        </div>
      )}
    </>
  );
}

/* ------------------------------------------------------------------ */
/*  RatesDisclaimer — indicative rates warning                         */
/* ------------------------------------------------------------------ */

export function RatesDisclaimer() {
  return (
    <div className="fa-bg-amber-50 fa-border fa-border-amber-300 fa-rounded-md fa-p-2 fa-text-xs fa-text-amber-800 fa-text-center fa-my-2">
      Premiums shown are indicative and subject to final underwriter confirmation.
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  IdentitySection — ICPAK member fields (manual input)               */
/* ------------------------------------------------------------------ */

export function IdentitySection({
  value,
  onChange,
  errors = {},
  onBlur,
}: {
  value: {
    icpakMemberNumber: string;
    firstName: string;
    lastName: string;
    phone: string;
    email: string;
  };
  onChange: (v: typeof value) => void;
  errors?: Record<string, string>;
  onBlur?: (field: string) => void;
}) {
  const set = (k: keyof typeof value) => (e: React.ChangeEvent<HTMLInputElement>) =>
    onChange({ ...value, [k]: e.target.value });
  const blur = (k: string) => () => onBlur?.(k);

  return (
    <div className="fa-section fa-section-orange fa-space-y-3">
      <Field
        id="icpakMemberNumber"
        label="ICPAK Member Number"
        error={errors.icpakMemberNumber}
        required
      >
        <input
          id="icpakMemberNumber"
          value={value.icpakMemberNumber}
          onChange={set("icpakMemberNumber")}
          onBlur={blur("icpakMemberNumber")}
          aria-required="true"
        />
      </Field>

      {/* Name + Phone + Email grid */}
      <div className="fa-grid fa-grid-cols-1 fa-md:grid-cols-2 fa-gap-3">
        <Field id="firstName" label="First Name" error={errors.firstName} required>
          <input
            id="firstName"
            value={value.firstName}
            onChange={set("firstName")}
            onBlur={blur("firstName")}
            aria-required="true"
          />
        </Field>
        <Field id="lastName" label="Last Name" error={errors.lastName} required>
          <input
            id="lastName"
            value={value.lastName}
            onChange={set("lastName")}
            onBlur={blur("lastName")}
            aria-required="true"
          />
        </Field>
        <Field id="phone" label="Phone Number" error={errors.phone} required>
          <input
            id="phone"
            value={value.phone}
            onChange={set("phone")}
            onBlur={blur("phone")}
            placeholder="0712345678 or +254712345678"
            aria-required="true"
          />
        </Field>
        <Field id="email" label="Email Address" error={errors.email} required>
          <input
            id="email"
            type="email"
            value={value.email}
            onChange={set("email")}
            onBlur={blur("email")}
            aria-required="true"
          />
        </Field>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  StepIndicator — wizard progress bar                                */
/* ------------------------------------------------------------------ */

export function StepIndicator({
  steps,
  currentIndex,
  completedSteps,
  onStepClick,
}: {
  steps: Array<{ key: string; label: string }>;
  currentIndex: number;
  completedSteps: Set<number>;
  onStepClick: (index: number) => void;
}) {
  const maxReachable = Math.max(...Array.from(completedSteps), -1) + 1;

  return (
    <div className="fa-mb-6">
      {/* Desktop version */}
      <nav role="navigation" aria-label="Form steps" className="fa-hidden fa-md:block">
        <ol className="fa-flex fa-items-center fa-justify-center fa-gap-0">
          {steps.map((step, i) => {
            const isCompleted = completedSteps.has(i);
            const isCurrent = i === currentIndex;
            const isClickable = i <= maxReachable;

            return (
              <li key={step.key} className="fa-flex fa-items-center">
                {/* Connector line before (not for the first step) */}
                {i > 0 && (
                  <div
                    className={`fa-w-12 fa-h-0.5 ${
                      isCompleted || isCurrent ? "fa-bg-faMaroon" : "fa-bg-slate-300"
                    }`}
                  />
                )}
                <button
                  type="button"
                  onClick={() => isClickable && onStepClick(i)}
                  disabled={!isClickable}
                  aria-current={isCurrent ? "step" : undefined}
                  className={`fa-flex fa-flex-col fa-items-center fa-gap-1 fa-px-2 ${
                    isClickable ? "fa-cursor-pointer" : "fa-cursor-default fa-opacity-50"
                  }`}
                >
                  {/* Circle */}
                  <span
                    className={`fa-flex fa-items-center fa-justify-center fa-w-8 fa-h-8 fa-rounded-full fa-text-sm fa-font-semibold fa-shrink-0 ${
                      isCompleted
                        ? "fa-bg-faMaroon fa-text-white"
                        : isCurrent
                          ? "fa-bg-faOrange fa-text-white"
                          : "fa-border-2 fa-border-slate-300 fa-text-slate-400"
                    }`}
                  >
                    {isCompleted ? "✓" : i + 1}
                  </span>
                  {/* Label */}
                  <span
                    className={`fa-text-xs fa-text-center fa-whitespace-nowrap ${
                      isCompleted
                        ? "fa-text-faMaroon fa-font-medium"
                        : isCurrent
                          ? "fa-text-faOrange fa-font-medium"
                          : "fa-text-slate-400"
                    }`}
                  >
                    {step.label}
                  </span>
                </button>
              </li>
            );
          })}
        </ol>
      </nav>

      {/* Mobile version */}
      <div className="fa-block fa-md:hidden">
        <div className="fa-text-sm fa-font-medium fa-text-faInk fa-text-center fa-mb-2">
          Step {currentIndex + 1} of {steps.length}: {steps[currentIndex]?.label}
        </div>
        <div className="fa-w-full fa-bg-slate-200 fa-rounded-full fa-h-2">
          <div
            className="fa-bg-faMaroon fa-h-2 fa-rounded-full fa-transition-all fa-duration-300"
            style={{ width: `${((currentIndex + 1) / steps.length) * 100}%` }}
          />
        </div>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  StepNavigation — Back / Next / Submit button row                   */
/* ------------------------------------------------------------------ */

export function StepNavigation({
  isFirstStep,
  isLastStep,
  onBack,
  onNext,
  onSubmit,
  nextDisabled,
  submitLoading,
  submitDisabled,
  nextLabel = "Next",
  submitLabel = "Submit",
}: {
  isFirstStep: boolean;
  isLastStep: boolean;
  onBack: () => void;
  onNext: () => void;
  onSubmit: () => void;
  nextDisabled?: boolean;
  submitLoading?: boolean;
  submitDisabled?: boolean;
  nextLabel?: string;
  submitLabel?: string;
}) {
  return (
    <div className="fa-flex fa-justify-between fa-items-center fa-mt-6">
      {!isFirstStep ? (
        <button
          type="button"
          onClick={onBack}
          className="fa-text-sm fa-text-faInk fa-font-medium fa-px-4 fa-py-2 hover:fa-underline"
        >
          ← Back
        </button>
      ) : (
        <div />
      )}
      {isLastStep ? (
        <SubmitButton onClick={onSubmit} loading={submitLoading} disabled={submitDisabled}>
          {submitLabel}
        </SubmitButton>
      ) : (
        <button
          type="button"
          className="fa-btn-primary"
          disabled={nextDisabled}
          onClick={onNext}
        >
          {nextLabel} →
        </button>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  ReviewSummary — read-only summary of form inputs                   */
/* ------------------------------------------------------------------ */

export function ReviewSummary({
  sections,
}: {
  sections: Array<{
    title: string;
    fields: Array<{
      label: string;
      value: string;
    }>;
  }>;
}) {
  return (
    <div className="fa-space-y-4">
      {sections.map((section) => (
        <div
          key={section.title}
          className="fa-bg-slate-50 fa-rounded-md fa-border fa-border-slate-200 fa-overflow-hidden"
        >
          <div className="fa-bg-slate-100 fa-px-4 fa-py-2 fa-font-semibold fa-text-sm fa-text-faInk fa-uppercase fa-tracking-wide">
            {section.title}
          </div>
          <div className="fa-grid fa-grid-cols-1 fa-md:grid-cols-2 fa-gap-x-6 fa-gap-y-2 fa-p-4">
            {section.fields.map((field) => (
              <div key={field.label} className="fa-flex fa-flex-col">
                <span className="fa-text-xs fa-text-slate-500">{field.label}</span>
                <span className="fa-text-sm fa-font-medium fa-text-faInk">
                  {field.value || "—"}
                </span>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  QuoteTable — premium breakdown with KES formatting                 */
/* ------------------------------------------------------------------ */

export function QuoteTable({ title, quote }: { title: string; quote: Quote }) {
  return (
    <div className="fa-border fa-border-slate-200 fa-rounded-md fa-overflow-hidden fa-my-4">
      <div className="fa-text-center fa-uppercase fa-py-2 fa-bg-slate-50 fa-font-semibold">
        {title}
      </div>
      <table className="fa-w-full">
        <tbody>
          {quote.items.map((i) => (
            <tr key={i.code} className="fa-border-t fa-border-slate-100">
              <td className="fa-p-2">{i.label}</td>
              <td className="fa-p-2 fa-text-right fa-tabular-nums">
                {i.note ? (
                  <span className="fa-text-faMaroon fa-text-sm">{i.note}</span>
                ) : (
                  `KES ${i.amountKes.toLocaleString()}`
                )}
              </td>
            </tr>
          ))}
          <tr className="fa-border-t">
            <td className="fa-p-2">Levies (0.45%)</td>
            <td className="fa-p-2 fa-text-right fa-tabular-nums">
              KES {quote.leviesKes.toLocaleString()}
            </td>
          </tr>
          <tr className="fa-border-t fa-border-slate-100">
            <td className="fa-p-2">Stamp Duty</td>
            <td className="fa-p-2 fa-text-right fa-tabular-nums">
              KES {quote.stampDutyKes.toLocaleString()}
            </td>
          </tr>
          <tr className="fa-border-t fa-font-bold">
            <td className="fa-p-2">Annual Premium</td>
            <td className="fa-p-2 fa-text-right fa-tabular-nums">
              KES {quote.totalKes.toLocaleString()}
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  FileInput — document upload with filename feedback                 */
/* ------------------------------------------------------------------ */

export function FileInput({
  reference,
  kind,
  label,
  onStatusChange,
}: {
  reference: string;
  kind: string;
  label: string;
  onStatusChange?: (kind: string, uploading: boolean) => void;
}) {
  const api = useApiBase();
  const [status, setStatus] = useState<"idle" | "uploading" | "ok" | "err">("idle");
  const [err, setErr] = useState("");
  const [fileName, setFileName] = useState("");

  async function onChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    setFileName(file.name);
    setStatus("uploading");
    onStatusChange?.(kind, true);
    try {
      await uploadDocument(api, reference, file, kind);
      setStatus("ok");
    } catch (e) {
      setErr((e as Error).message);
      setStatus("err");
    } finally {
      onStatusChange?.(kind, false);
    }
  }

  return (
    <div>
      <label className="fa-uppercase fa-text-xs fa-font-medium fa-block fa-mb-1">{label}</label>
      <input
        type="file"
        accept="application/pdf,image/jpeg,image/png"
        onChange={onChange}
        disabled={status === "uploading"}
        aria-label={label}
      />
      {status === "uploading" && (
        <div className="fa-text-xs fa-text-slate-600 fa-mt-0.5">Uploading {fileName}…</div>
      )}
      {status === "ok" && (
        <div className="fa-text-xs fa-text-green-700 fa-mt-0.5">
          {fileName} uploaded ✓
        </div>
      )}
      {status === "err" && (
        <div className="fa-text-xs fa-text-red-700 fa-mt-0.5">{err}</div>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  DocumentUploadSection — conditional doc upload with gating         */
/* ------------------------------------------------------------------ */

export function DocumentUploadSection({
  reference,
  documents,
  requiredPayment,
  paymentConfirmed,
  onSubmitDocs,
  isSubmittingDocs,
  docsSubmitted,
  existingDocs,
}: {
  reference: string;
  documents: Array<{ kind: string; label: string }>;
  requiredPayment: boolean;
  paymentConfirmed: boolean;
  onSubmitDocs: () => void;
  isSubmittingDocs: boolean;
  docsSubmitted: boolean;
  existingDocs?: MemberDocument[];
}) {
  const [uploading, setUploading] = useState<Set<string>>(new Set());

  function handleUploadStatus(kind: string, isUploading: boolean) {
    setUploading((prev) => {
      const next = new Set(prev);
      if (isUploading) next.add(kind);
      else next.delete(kind);
      return next;
    });
  }

  const anyUploading = uploading.size > 0;

  if (requiredPayment && !paymentConfirmed) {
    return (
      <p className="fa-text-sm fa-text-slate-500 fa-italic fa-mt-3">
        Complete payment above to unlock document upload.
      </p>
    );
  }

  if (docsSubmitted) {
    return (
      <div className="fa-bg-green-50 fa-border fa-border-green-300 fa-rounded-md fa-p-4 fa-text-center fa-mt-4">
        <p className="fa-text-green-800 fa-font-semibold">Documents submitted successfully ✓</p>
        <p className="fa-text-green-700 fa-text-sm">We will review and get back to you shortly.</p>
      </div>
    );
  }

  return (
    <div className="fa-section fa-bg-white fa-border fa-border-slate-200 fa-mt-4">
      <h3 className="fa-text-center fa-text-faOrange fa-text-xl fa-font-semibold">
        Upload your documents
      </h3>
      <div className="fa-grid fa-grid-cols-1 fa-md:grid-cols-2 fa-gap-3 fa-mt-3">
        {documents.map((doc) => {
          const existing = existingDocs?.find((e) => e.kind === doc.kind);
          return (
            <div key={doc.kind}>
              {existing && (
                <div className="fa-flex fa-items-center fa-gap-1 fa-mb-1">
                  <span className="fa-text-green-700 fa-text-xs fa-font-medium">
                    ✓ {existing.name}
                  </span>
                  <span className="fa-text-slate-400 fa-text-xs">(on file)</span>
                </div>
              )}
              <FileInput
                reference={reference}
                kind={doc.kind}
                label={existing ? `Replace ${doc.label} (optional)` : doc.label}
                onStatusChange={handleUploadStatus}
              />
            </div>
          );
        })}
      </div>
      {anyUploading && (
        <p className="fa-text-xs fa-text-amber-700 fa-text-center fa-mt-2">
          Please wait for all uploads to finish before submitting.
        </p>
      )}
      <div className="fa-flex fa-justify-center fa-mt-4">
        <SubmitButton
          onClick={onSubmitDocs}
          loading={isSubmittingDocs}
          disabled={anyUploading}
          variant="orange"
        >
          {anyUploading ? "Uploading…" : "Submit Documentation"}
        </SubmitButton>
      </div>
    </div>
  );
}
